import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@reservamos/elements';
import PropTypes from 'prop-types';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { trackRescheduleAttempt } from 'metrics/user-analytics/exchange';
import RescheduleModal from './RescheduleModal';

/**
 * RescheduleSection component.
 *
 * @param {object} props - selectedPassengerToAlter, allPassengers, rescheduleTicket, isTicketSelected, validateExchange, operationNumbers, email, document, onRefreshData.
 * @param {object} props.selectedPassengerToAlter - Selected passenger to alter the ticket for.
 * @param {object} props.allPassengers - Object containing all passengers data.
 * @param {Function} props.rescheduleTicket - Function to reschedule a ticket.
 * @param {boolean} props.isTicketSelected - Whether a ticket is currently selected.
 * @param {Function} props.validateExchange - Function to validate the exchange.
 * @param {string} props.operationNumbers - Operation numbers for the ticket.
 * @param {string} props.email - Email address.
 * @param {string} props.document - Document number.
 * @param {Function} props.onRefreshData - Function to refresh ticket data and clear selection.
 *
 * @returns {JSX.Element|null} The rendered reschedule section or null if feature is disabled.
 */
const RescheduleSection = ({
  selectedPassengerToAlter,
  allPassengers,
  rescheduleTicket,
  isTicketSelected,
  operationNumbers,
  email,
  onRefreshData,
}) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [rescheduleResponseData, setRescheduleResponseData] = useState(null);

  /**
   * handleRescheduleSelectedTickets Function
   *
   * Handles the opening of the reschedule modal when a ticket is selected.
   *
   * This function is called when the user clicks the reschedule button. It checks
   * if a ticket is selected and either opens the modal or shows an error message.
   *
   * @returns {void}
   */
  const handleRescheduleSelectedTickets = useCallback(() => {
    if (selectedPassengerToAlter.ticketKey !== '') {
      setShowRescheduleModal(true);
    }
  }, [selectedPassengerToAlter.ticketKey]);

  /**
   * handleRescheduleModalConfirm Function
   *
   * Handles the confirmation of ticket rescheduling within the modal.
   *
   * This async function is called when the user confirms the rescheduling of selected tickets
   * in the modal. It processes the rescheduling and performs any necessary asynchronous operations.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when the rescheduling process is completed.
   */
  const handleRescheduleModalConfirm = useCallback(async () => {
    const { ticketKey, nodeTripName } = selectedPassengerToAlter;

    if (!ticketKey) return;

    const group = allPassengers[nodeTripName];
    const ticket = group?.find(({ transporterKey }) => transporterKey === ticketKey);

    if (!ticket) return;

    const {
      companyCode,
      documentType,
      documentPrefix,
      documentNumber,
      email: ticketEmail,
    } = ticket;

    setIsRescheduleLoading(true);
    setRescheduleResponseData(null);

    try {
      const rescheduleResponse = await rescheduleTicket({
        companyCode,
        documentType,
        documentPrefix,
        documentNumber,
        email: ticketEmail ?? email,
        reserveCode: operationNumbers,
      });

      if (rescheduleResponse.status) {
        trackRescheduleAttempt(true);
        setRescheduleResponseData({
          success: true,
          message: t('ticket_list.reschedule_success'),
        });
      } else {
        const errorMsg = rescheduleResponse.message || t('ticket_list.reschedule_error');
        trackRescheduleAttempt(false, errorMsg);
        setRescheduleResponseData({
          success: false,
          message: errorMsg,
        });
      }
    } catch (error) {
      trackRescheduleAttempt(false, t('ticket_list.reschedule_error'));
      setRescheduleResponseData({
        success: false,
        message: t('ticket_list.reschedule_error'),
      });
    } finally {
      setIsRescheduleLoading(false);
    }
  }, [selectedPassengerToAlter, allPassengers, rescheduleTicket, t, email, operationNumbers]);

  /**
   * handleRescheduleModalQuit Function
   *
   * Handles the closing or quitting of the reschedule modal.
   *
   * This function is called when the user decides to exit the modal without completing
   * the rescheduling process, and it performs the necessary actions to close or clean up the modal state.
   * If there was a response from the reschedule operation, it refreshes the ticket data.
   *
   * @returns {void}
   */
  const handleRescheduleModalQuit = useCallback(() => {
    setShowRescheduleModal(false);

    // If there was a response, refresh the ticket data and clear selection
    if (rescheduleResponseData && onRefreshData) {
      onRefreshData();
    }

    setRescheduleResponseData(null);
  }, [rescheduleResponseData, onRefreshData]);

  // Don't render if the feature is disabled
  if (!features.SHOW_RESCHEDULE_TICKET_BUTTON) {
    return null;
  }

  return (
    <>
      <Button
        buttonType="submit"
        text={t('ticket_list.button_change')}
        variant="accent"
        isRounded
        withHeight
        fullWidthOnSmall
        className="inline-flex w-auto"
        onClick={handleRescheduleSelectedTickets}
        isDisabled={!isTicketSelected}
      />
      <RescheduleModal
        showDialog={showRescheduleModal}
        onConfirm={handleRescheduleModalConfirm}
        onCancel={handleRescheduleModalQuit}
        isLoading={isRescheduleLoading}
        responseData={rescheduleResponseData}
      />
    </>
  );
};

RescheduleSection.propTypes = {
  selectedPassengerToAlter: PropTypes.object.isRequired,
  allPassengers: PropTypes.object.isRequired,
  rescheduleTicket: PropTypes.func.isRequired,
  isTicketSelected: PropTypes.bool.isRequired,
  operationNumbers: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onRefreshData: PropTypes.func,
};

export default RescheduleSection;
